import { useEffect, useMemo, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PencilIcon } from 'lucide-react';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminCustomer, CustomerCategoryKey } from '@mds/shared/types/customer';

import { useSession } from '@/auth/use-session';
import CustomerForm from '@/components/customers/customer-form';
import CustomerRecord from '@/components/customers/customer-record';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { controlCls } from '@/components/ui/field';
import IconButton from '@mds/shared/ui/icon-button';
import Modal from '@/components/ui/modal';
import Select from '@mds/shared/ui/select';
import { Table, Row, Cell } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import * as customers from '@/customers/customers.api';
import { TAG_POOL } from '@/data/customers';
import {
  CUSTOMER_CATEGORIES,
  blankCustomer,
  changesBetween,
  createRequestOf,
  customerDraftOf,
  customerSchema,
  type CustomerDraft,
} from '@/domain/customers';
import { dateStamp } from '@mds/shared/lib/dates';
import { useValidation } from '@/lib/validation';

const SORTS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'name:asc', label: 'Name A–Z' },
  { value: 'name:desc', label: 'Name Z–A' },
  { value: 'reference:asc', label: 'Reference' },
  { value: 'updatedAt:desc', label: 'Recently edited' },
];

const ALL = 'All';
const PAGE_SIZE = 25;

/**
 * The customer database.
 *
 * Searching, filtering, sorting and paging all happen on the server, so the
 * page holds one page of rows rather than the whole directory. `reference`
 * (CU-301) is the identifier the front desk reads out; the UUID stays in the
 * URL of the request and nowhere else.
 */
export default function CustomersPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const mayManage = can('crm.customers.manage');

  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>(ALL);
  const [tag, setTag] = useState<string>(ALL);
  const [sort, setSort] = useState('createdAt:desc');

  const [form, setForm] = useState<CustomerDraft>(blankCustomer);
  const [editing, setEditing] = useState<AdminCustomer | null>(null);
  const [open, setOpen] = useState(false);
  const [record, setRecord] = useState<AdminCustomer | null>(null);
  const [deleting, setDeleting] = useState<AdminCustomer | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const validation = useValidation(customerSchema, form);
  const problemRef = useRef<HTMLParagraphElement>(null);

  // The form is long enough to scroll, and Save sits at the bottom of it, so
  // an error at the top would otherwise land off-screen.
  useEffect(() => {
    if (problem) problemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [problem]);

  // Typing is not a page of results each keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const [sortKey, direction] = sort.split(':') as [
    'reference' | 'name' | 'createdAt' | 'updatedAt',
    'asc' | 'desc',
  ];

  const list = useQuery({
    queryKey: ['customers', { page, search, category, tag, sort }],
    queryFn: () =>
      customers.listCustomers({
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
        category: category === ALL ? undefined : (category as CustomerCategoryKey),
        tag: tag === ALL ? undefined : tag,
        sort: sortKey,
        direction,
      }),
  });

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const pagination = list.data?.pagination;

  /**
   * The API has no endpoint for the tag vocabulary, so the filter offers the
   * suggested tags plus whatever this page of results actually carries.
   */
  const tagOptions = useMemo(
    () => [...new Set([...TAG_POOL, ...rows.flatMap((c) => c.tags)])].sort(),
    [rows],
  );

  const startAdd = () => {
    setEditing(null);
    setForm(blankCustomer);
    setProblem(null);
    validation.reset();
    setOpen(true);
  };

  const startEdit = (c: AdminCustomer) => {
    setEditing(c);
    setForm(customerDraftOf(c));
    setProblem(null);
    validation.reset();
    setOpen(true);
  };

  const save = async () => {
    if (!validation.check()) return;

    setBusy(true);
    setProblem(null);
    try {
      if (editing) {
        const patch = changesBetween(customerDraftOf(editing), form);
        // PATCH rejects an empty body, and an untouched form is not an error.
        const saved =
          Object.keys(patch).length === 0
            ? editing
            : await customers.updateCustomer(editing.id, patch);
        setRecord((cur) => (cur && cur.id === saved.id ? saved : cur));
      } else {
        await customers.createCustomer(createRequestOf(form));
        // A new record sorts to the top of the default ordering.
        setPage(1);
      }
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      setForm(blankCustomer);
      setEditing(null);
      setOpen(false);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;

    setBusy(true);
    setProblem(null);
    try {
      await customers.deleteCustomer(deleting.id);
      queryClient.removeQueries({ queryKey: ['customer', deleting.id] });
      if (rows.length === 1 && page > 1) setPage((current) => current - 1);
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDeleting(null);
    } catch (e) {
      setProblem(
        e instanceof ApiError && e.code === 'CUSTOMER_HAS_HISTORY'
          ? 'This customer has booking or facility proposal history, so the record must stay.'
          : e instanceof ApiError
            ? e.message
            : 'That did not delete. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card
        title={
          pagination
            ? `${pagination.total} ${pagination.total === 1 ? 'customer' : 'customers'}`
            : 'Customers'
        }
        action={
          <Toolbar>
            <input
              placeholder="Name, contact, tag or interest"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search customers"
              className={controlCls + ' w-[280px]'}
            />
            {/* Both filters feed the communications module, which picks its
                recipients by category and tag. */}
            <Select
              value={category}
              onChange={(v) => {
                setCategory(v);
                setPage(1);
              }}
              options={[
                { value: ALL, label: 'All categories' },
                ...CUSTOMER_CATEGORIES.map((c) => ({ value: c.key, label: c.name })),
              ]}
              aria-label="Filter by category"
            />
            <Select
              value={tag}
              onChange={(v) => {
                setTag(v);
                setPage(1);
              }}
              options={[
                { value: ALL, label: 'All tags' },
                ...tagOptions.map((t) => ({ value: t, label: t })),
              ]}
              aria-label="Filter by tag"
            />
            <Select
              value={sort}
              onChange={(v) => {
                setSort(v);
                setPage(1);
              }}
              options={SORTS}
              aria-label="Sort customers"
            />
            {mayManage && <Button onClick={startAdd}>Add customer</Button>}
          </Toolbar>
        }
      >
        {list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading customers…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load the customers"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No customer found"
            hint="Try a different search, clear the filters, or add them as a new record."
          />
        ) : (
          <>
            <Table head={['Ref', 'Name', 'Contact', 'Category', 'Tags', 'Joined', '']}>
              {rows.map((c) => (
                <Row key={c.id} onClick={() => setRecord(c)}>
                  <Cell className="font-mono text-md text-ink-muted">{c.reference}</Cell>
                  <Cell className="font-semibold">{c.name}</Cell>
                  <Cell>
                    <div className="flex flex-col">
                      <span>{c.email || <span className="text-ink-muted">—</span>}</span>
                      <span className="font-mono text-md text-ink-muted">{c.phone}</span>
                    </div>
                  </Cell>
                  <Cell>
                    <Chip>{c.category.name}</Chip>
                  </Cell>
                  <Cell>
                    <div className="flex flex-wrap gap-1.5">
                      {c.tags.length === 0 ? (
                        <span className="text-ink-muted">—</span>
                      ) : (
                        c.tags.map((t) => (
                          <Chip key={t} className="text-md">
                            {t}
                          </Chip>
                        ))
                      )}
                    </div>
                  </Cell>
                  <Cell className="font-mono text-md text-ink-muted">{dateStamp(c.joinedAt)}</Cell>
                  <Cell>
                    {mayManage && (
                      <IconButton
                        label={`Edit ${c.name}`}
                        onClick={(e) => {
                          // The row opens the record; the pencil goes straight
                          // to the editor without it.
                          e.stopPropagation();
                          startEdit(c);
                        }}
                      >
                        <PencilIcon className="size-5" strokeWidth={2.2} aria-hidden="true" />
                      </IconButton>
                    )}
                  </Cell>
                </Row>
              ))}
            </Table>

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-md text-ink-muted">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="quiet"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={!pagination.hasMore}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        open={!!record}
        onClose={() => setRecord(null)}
        title={record?.name ?? ''}
        footer={
          record ? (
            <>
              {mayManage && (
                <>
                  <Button
                    onClick={() => {
                      startEdit(record);
                      setRecord(null);
                    }}
                  >
                    Edit record
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setProblem(null);
                      setDeleting(record);
                      setRecord(null);
                    }}
                  >
                    Delete customer
                  </Button>
                </>
              )}
              <Button variant="quiet" onClick={() => setRecord(null)}>
                Close
              </Button>
            </>
          ) : null
        }
      >
        {record && (
          <CustomerRecord
            customer={record}
            onSaved={(saved) => {
              setRecord(saved);
              void queryClient.invalidateQueries({ queryKey: ['customers'] });
            }}
          />
        )}
      </Modal>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? 'Edit customer' : 'Add customer'}
        footer={
          <>
            <Button onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Add customer'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </>
        }
      >
        {editing && <p className="m-0 font-mono text-md text-ink-muted">{editing.reference}</p>}

        {problem && (
          <p
            ref={problemRef}
            role="alert"
            className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
          >
            {problem}
          </p>
        )}

        <CustomerForm
          draft={form}
          onChange={setForm}
          editing={!!editing}
          field={validation.field}
        />
      </Modal>

      <Modal
        open={deleting !== null}
        onClose={() => !busy && setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'customer'}?`}
        footer={
          <>
            <Button variant="danger" disabled={busy} onClick={() => void remove()}>
              {busy ? 'Deleting…' : 'Delete customer'}
            </Button>
            <Button variant="quiet" disabled={busy} onClick={() => setDeleting(null)}>
              Keep customer
            </Button>
          </>
        }
      >
        {problem && (
          <p
            role="alert"
            className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
          >
            {problem}
          </p>
        )}
        <p className="m-0 text-base text-ink-secondary">
          <strong>{deleting?.reference}</strong> will be removed permanently. Customers with booking
          or facility proposal history cannot be deleted because those records must keep their
          customer link.
        </p>
      </Modal>
    </div>
  );
}
